import { registerSW } from "virtual:pwa-register";

/** Installed apps often stay open for days, so look for a new version this often too. */
const CHECK_EVERY_MS = 60 * 60 * 1000;

/**
 * Registers the service worker that keeps every file for offline use. It
 * registers after the load event, so its download does not compete with the
 * game's own start. `onUpdate` hears of each new version that is waiting; calling
 * its `apply` switches to it and reloads the page.
 */
export function registerOffline(onUpdate: (apply: () => Promise<void>) => void): void {
  registerSW({
    onNeedRefresh: () => {
      // once one tab applies the update, every tab that offered it follows
      navigator.serviceWorker.addEventListener("controllerchange", reloadOnce);
      onUpdate(applyUpdate);
    },
    // The plugin's own reload only covers tabs that had a worker when they
    // opened, so not the tab of a first visit; applyUpdate covers every tab.
    onNeedReload: () => undefined,
    onRegisteredSW: (_url, reg) => {
      if (reg) watchForUpdates(reg);
    },
  });
  // Firefox asks the reader before granting this, so only ask once the app is installed
  if (matchMedia("(display-mode: standalone)").matches) void navigator.storage?.persist?.();
}

/** Lets the waiting version take over, then reloads the page into it. */
async function applyUpdate(): Promise<void> {
  const waiting = (await navigator.serviceWorker.getRegistration())?.waiting;
  // another tab was quicker; the new version is already in charge
  if (!waiting) return reloadOnce();
  waiting.addEventListener("statechange", () => {
    if (waiting.state === "activated") reloadOnce();
  });
  waiting.postMessage({ type: "SKIP_WAITING" });
}

let reloading = false;
function reloadOnce(): void {
  if (reloading) return;
  reloading = true;
  location.reload();
}

/** The browser checks on each page load; an app left open also checks hourly and on return. */
function watchForUpdates(reg: ServiceWorkerRegistration): void {
  const check = (): void => {
    // a failed check (offline, server down) changes nothing; the next one tries again
    if (navigator.onLine) reg.update().catch(() => undefined);
  };
  setInterval(check, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}
